import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Clock, ChevronRight, ArrowLeft, Gamepad2, Mail, Check } from '../lib/icons';
import { getPostBySlug, BLOG_POSTS, type BlogSection } from '../data/blog-posts';
import SEOHead from '../components/SEOHead';
import ImageSlot from '../components/ImageSlot';

// ── Section renderers ──────────────────────────────────────────────────────
const TextSection = ({ section }: { section: BlogSection }) => (
  <div className="space-y-4">
    {section.heading && (
      <h2 className="text-2xl font-bold text-[#E0E0E0] mt-10 mb-4">{section.heading}</h2>
    )}
    {section.paragraphs?.map((p, i) => (
      <p key={i} className="text-[#B0B0B0] leading-relaxed text-[1.05rem]">{p}</p>
    ))}
  </div>
);

const BulletsSection = ({ section }: { section: BlogSection }) => (
  <div>
    {section.heading && (
      <h2 className="text-2xl font-bold text-[#E0E0E0] mt-10 mb-4">{section.heading}</h2>
    )}
    <ul className="space-y-2.5">
      {section.items?.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-[#B0B0B0]">
          <Check className="h-4 w-4 text-[#39FF14] shrink-0 mt-1" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CalloutSection = ({ section }: { section: BlogSection }) => (
  <div className="my-6 bg-[#39FF14]/8 border border-[#39FF14]/30 rounded-xl p-5">
    {section.heading && (
      <h2 className="text-2xl font-bold text-[#E0E0E0] mt-10 mb-4">{section.heading}</h2>
    )}
    <p className="text-[#E0E0E0] font-medium leading-relaxed">{section.callout}</p>
  </div>
);

const ComparisonSection = ({ section }: { section: BlogSection }) => {
  if (!section.comparisonRows || !section.comparisonHeaders) return null;
  const [col0, col1, col2] = section.comparisonHeaders;
  return (
    <div className="my-6">
      {section.heading && (
        <h2 className="text-2xl font-bold text-[#E0E0E0] mt-10 mb-4">{section.heading}</h2>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-900 border-b border-gray-800">
              <th className="text-left px-4 py-3 text-[#B0B0B0] font-semibold">{col0}</th>
              <th className="text-left px-4 py-3 text-[#00BFFF] font-semibold">{col1}</th>
              <th className="text-left px-4 py-3 text-[#39FF14] font-semibold">{col2}</th>
            </tr>
          </thead>
          <tbody>
            {section.comparisonRows.map((row, i) => (
              <tr key={i} className="border-b border-gray-800 last:border-0 hover:bg-gray-900/50 transition-colors">
                <td className="px-4 py-3 text-[#E0E0E0] font-medium">{row.label}</td>
                <td className="px-4 py-3 text-[#B0B0B0]">{row.a}</td>
                <td className="px-4 py-3 text-[#B0B0B0]">{row.b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CTASection = ({ section }: { section: BlogSection }) => {
  if (!section.cta) return null;
  const { heading, body, primary, secondary } = section.cta;
  return (
    <div className="my-8 bg-gray-900 border border-[#39FF14]/30 rounded-2xl p-7 text-center">
      <Gamepad2 className="h-8 w-8 text-[#39FF14] mx-auto mb-3" />
      <h3 className="text-xl font-bold text-[#E0E0E0] mb-2">{heading}</h3>
      {body && <p className="text-[#B0B0B0] text-sm mb-5 max-w-md mx-auto">{body}</p>}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {primary.href.startsWith('mailto') ? (
          <a
            href={primary.href}
            className="inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-bold px-6 py-3 rounded-lg transition-all text-sm"
          >
            {primary.label} <ChevronRight className="h-4 w-4" />
          </a>
        ) : (
          <Link
            to={primary.href}
            className="inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-bold px-6 py-3 rounded-lg transition-all text-sm"
          >
            {primary.label} <ChevronRight className="h-4 w-4" />
          </Link>
        )}
        {secondary && (
          secondary.href.startsWith('mailto') ? (
            <a
              href={secondary.href}
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-6 py-3 rounded-lg border border-gray-700 hover:border-[#39FF14] transition-all text-sm"
            >
              <Mail className="h-4 w-4" /> {secondary.label}
            </a>
          ) : (
            <Link
              to={secondary.href}
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-6 py-3 rounded-lg border border-gray-700 hover:border-[#39FF14] transition-all text-sm"
            >
              <Mail className="h-4 w-4" /> {secondary.label}
            </Link>
          )
        )}
      </div>
    </div>
  );
};

const renderSection = (section: BlogSection, i: number) => {
  switch (section.type) {
    case 'text':        return <TextSection key={i} section={section} />;
    case 'bullets':     return <BulletsSection key={i} section={section} />;
    case 'callout':     return <CalloutSection key={i} section={section} />;
    case 'comparison':  return <ComparisonSection key={i} section={section} />;
    case 'cta':         return <CTASection key={i} section={section} />;
    default:            return null;
  }
};

// ── Page ──────────────────────────────────────────────────────────────────
const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter(p => p.slug !== post.slug && p.categorySlug === post.categorySlug).slice(0, 2);
  const others = related.length < 2 ? [...related, ...BLOG_POSTS.filter(p => p.slug !== post.slug && !related.find(r => r.slug === p.slug))].slice(0, 2) : related;

  const articleJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.publishDate,
    author: { '@type': 'Organization', name: 'GameFix Solutions' },
    publisher: {
      '@type': 'Organization',
      name: 'GameFix Solutions',
      url: 'https://gamefixsolutions.com',
    },
    keywords: post.keywords.join(', '),
  };

  return (
    <div className="bg-[#0D0D0D] min-h-screen">
      <SEOHead
        title={post.metaTitle}
        description={post.metaDescription}
        keywords={post.keywords.join(', ')}
        canonical={`https://gamefixsolutions.com/blog/${post.slug}`}
        jsonLD={articleJsonLD}
      />

      {/* Post header */}
      <div
        className="border-b border-gray-900 py-16"
        style={{ background: `linear-gradient(135deg, ${post.categoryColor}15 0%, #0D0D0D 60%)` }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-[#B0B0B0] hover:text-[#39FF14] text-sm font-medium mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> All Articles
          </Link>
          <span
            className="inline-block text-xs font-bold px-3 py-1 rounded-full mb-4"
            style={{ backgroundColor: `${post.categoryColor}20`, color: post.categoryColor, border: `1px solid ${post.categoryColor}44` }}
          >
            {post.category}
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-[#E0E0E0] leading-tight mb-5">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-[#B0B0B0]">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              <span>{post.readTime}</span>
            </div>
            <span>·</span>
            <span>GameFix Solutions</span>
          </div>
        </div>
      </div>

      {/* Cover image */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10">
        <div className="aspect-video rounded-2xl overflow-hidden">
          <ImageSlot
            slotId={`blog-cover-${post.slug}`}
            fallbackSrc={post.image ?? ''}
            fallbackAlt={post.title}
            className="object-cover"
            wrapperClassName="w-full h-full"
          />
        </div>
      </div>

      {/* Article body */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-14 space-y-6">
        {post.sections.map(renderSection)}
      </article>

      {/* Final CTA */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <div className="bg-gradient-to-r from-gray-900 to-black rounded-2xl border border-[#39FF14]/30 p-8 text-center">
          <Gamepad2 className="h-10 w-10 text-[#39FF14] mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-[#E0E0E0] mb-2">Ready to Upgrade?</h2>
          <p className="text-[#B0B0B0] mb-6 max-w-sm mx-auto text-sm">
            San Diego · Temecula · Nationwide Mail-In · 90-Day Warranty
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/custom-controllers" className="inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-bold px-7 py-3 rounded-lg transition-all">
              <Gamepad2 className="h-4 w-4" /> Build My Controller
            </Link>
            <Link to="/mail-in-repairs" className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-7 py-3 rounded-lg border border-gray-700 hover:border-[#39FF14] transition-all">
              <Mail className="h-4 w-4" /> Mail-In Upgrade
            </Link>
          </div>
        </div>
      </div>

      {/* Related posts */}
      {others.length > 0 && (
        <div className="border-t border-gray-900 bg-black py-14">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h3 className="text-lg font-bold text-[#E0E0E0] mb-6">More Articles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {others.map(p => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group bg-gray-900 rounded-xl border border-gray-800 hover:border-[#39FF14]/40 p-5 transition-all"
                >
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full inline-block mb-2"
                    style={{ backgroundColor: `${p.categoryColor}20`, color: p.categoryColor, border: `1px solid ${p.categoryColor}40` }}
                  >
                    {p.category}
                  </span>
                  <h4 className="text-[#E0E0E0] font-semibold leading-snug mb-2 group-hover:text-[#39FF14] transition-colors text-sm">
                    {p.title}
                  </h4>
                  <div className="flex items-center gap-1 text-[#39FF14] text-xs font-semibold">
                    Read <ChevronRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPostPage;
